/**
 * Turning a user-agent string into something a person can recognise.
 *
 * User-agent strings are archaeology — every browser claims to be Mozilla,
 * Chrome claims to be Safari, Edge claims to be Chrome — so order of testing
 * matters more than the patterns do. Each check below has to run before the
 * one it impersonates.
 *
 * This is deliberately approximate. The point is "was that me, on my phone,
 * yesterday?" — not analytics. When it cannot tell, it says so rather than
 * guessing, because a confidently wrong device name would make someone
 * revoke the wrong session, or fail to revoke the right one.
 */

export type DeviceKind = 'desktop' | 'mobile' | 'tablet' | 'unknown';

export interface ParsedAgent {
  browser: string;
  os: string;
  device: DeviceKind;
  /** `Chrome on Windows`, or the raw string when nothing matched. */
  summary: string;
}

export function parseUserAgent(raw: string | null): ParsedAgent {
  if (!raw) {
    return { browser: 'Unknown', os: 'Unknown', device: 'unknown', summary: 'Unknown device' };
  }

  const browser = browserOf(raw);
  const os = osOf(raw);
  const device = deviceOf(raw);

  const summary =
    browser === 'Unknown' && os === 'Unknown'
      ? truncate(raw)
      : `${browser}${os === 'Unknown' ? '' : ` on ${os}`}`;

  return { browser, os, device, summary };
}

function browserOf(ua: string): string {
  // Edge and Opera both carry "Chrome"; Chrome carries "Safari". Most
  // specific first, or everything comes out as Safari.
  if (/\bEdg[A-Z]?\//.test(ua)) return 'Edge';
  if (/\bOPR\/|\bOpera\//.test(ua)) return 'Opera';
  if (/\bSamsungBrowser\//.test(ua)) return 'Samsung Internet';
  if (/\bFirefox\/|\bFxiOS\//.test(ua)) return 'Firefox';
  if (/\bChrome\/|\bCriOS\//.test(ua)) return 'Chrome';
  if (/\bSafari\//.test(ua) && /\bVersion\//.test(ua)) return 'Safari';
  if (/\bcurl\/|\bwget\//i.test(ua)) return 'Command line';
  if (/\bPostmanRuntime\//.test(ua)) return 'Postman';
  return 'Unknown';
}

function osOf(ua: string): string {
  if (/\bWindows NT 10/.test(ua)) return 'Windows';
  if (/\bWindows/.test(ua)) return 'Windows';
  if (/\biPhone\b/.test(ua)) return 'iPhone';
  if (/\biPad\b/.test(ua)) return 'iPad';
  // Must come after iPhone/iPad — both include "like Mac OS X".
  if (/\bMac OS X\b/.test(ua)) return 'macOS';
  if (/\bAndroid\b/.test(ua)) return 'Android';
  if (/\bCrOS\b/.test(ua)) return 'ChromeOS';
  if (/\bLinux\b/.test(ua)) return 'Linux';
  return 'Unknown';
}

function deviceOf(ua: string): DeviceKind {
  if (/\biPad\b/.test(ua) || (/\bAndroid\b/.test(ua) && !/\bMobile\b/.test(ua))) return 'tablet';
  if (/\bMobi|\biPhone\b|\bAndroid\b/.test(ua)) return 'mobile';
  if (/\bWindows|\bMac OS X\b|\bLinux\b|\bCrOS\b/.test(ua)) return 'desktop';
  return 'unknown';
}

function truncate(value: string): string {
  return value.length > 60 ? `${value.slice(0, 57)}…` : value;
}

/**
 * A readable address.
 *
 * Behind a reverse proxy on the same host, everything arrives as loopback.
 * Showing `::1` to a bookkeeper is noise; "This computer" is the truth they
 * can act on.
 */
export function describeAddress(ip: string | null): string {
  if (!ip) return 'Unknown address';
  if (ip === '::1' || ip === '127.0.0.1' || ip === '::ffff:127.0.0.1') return 'This computer';
  // IPv4-mapped IPv6 addresses are just IPv4 wearing a hat.
  return ip.startsWith('::ffff:') ? ip.slice(7) : ip;
}
